import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ButtonHTMLAttributes, KeyboardEvent, MouseEvent, ReactNode } from "react";
import "./Dropdown.css";

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface DropdownProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "value" | "defaultValue" | "onChange" | "type"
> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  invalid?: boolean;
  options: DropdownOption[];
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

const CaretIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

const findFirstEnabled = (options: DropdownOption[]) => options.findIndex((opt) => !opt.disabled);

const findLastEnabled = (options: DropdownOption[]) => {
  for (let i = options.length - 1; i >= 0; i -= 1) {
    if (!options[i]?.disabled) return i;
  }
  return -1;
};

export const Dropdown = forwardRef<HTMLButtonElement, DropdownProps>(function Dropdown(
  {
    label,
    helperText,
    errorText,
    invalid,
    options,
    placeholder,
    disabled,
    value,
    defaultValue,
    onChange,
    id,
    className,
    onKeyDown,
    onClick,
    ...rest
  },
  forwardedRef,
) {
  const autoId = useId();
  const dropdownId = id ?? `go4-dropdown-${autoId}`;
  const labelId = `${dropdownId}-label`;
  const helperId = `${dropdownId}-helper`;
  const errorId = `${dropdownId}-error`;
  const listboxId = `${dropdownId}-listbox`;
  const isInvalid = invalid || Boolean(errorText);
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? value : internalValue;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

  const setRef = (node: HTMLButtonElement | null) => {
    triggerRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef)
      (forwardedRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
  };

  const selectedIndex = useMemo(
    () => options.findIndex((opt) => opt.value === currentValue),
    [options, currentValue],
  );
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const openMenu = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : findFirstEnabled(options));
  }, [disabled, selectedIndex, options]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const commitValue = useCallback(
    (opt: DropdownOption) => {
      if (opt.disabled) return;
      if (!isControlled) setInternalValue(opt.value);
      onChange?.(opt.value);
      closeMenu();
      triggerRef.current?.focus();
    },
    [isControlled, onChange, closeMenu],
  );

  const moveActive = useCallback(
    (direction: 1 | -1) => {
      if (options.every((opt) => opt.disabled)) return;
      setActiveIndex((current) => {
        let next = current;
        do {
          next = (next + direction + options.length) % options.length;
        } while (options[next]?.disabled && next !== current);
        return next;
      });
    },
    [options],
  );

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent | globalThis.MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) closeMenu();
    };
    document.addEventListener("mousedown", handlePointerDown as EventListener);
    return () => document.removeEventListener("mousedown", handlePointerDown as EventListener);
  }, [open, closeMenu]);

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || disabled) return;

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (!open) openMenu();
        else moveActive(1);
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (!open) openMenu();
        else moveActive(-1);
        break;
      }
      case "Home": {
        if (open) {
          event.preventDefault();
          const first = findFirstEnabled(options);
          if (first >= 0) setActiveIndex(first);
        }
        break;
      }
      case "End": {
        if (open) {
          event.preventDefault();
          const last = findLastEnabled(options);
          if (last >= 0) setActiveIndex(last);
        }
        break;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        if (!open) openMenu();
        else if (activeIndex >= 0 && options[activeIndex]) commitValue(options[activeIndex]);
        break;
      }
      case "Escape": {
        if (open) {
          event.preventDefault();
          closeMenu();
        }
        break;
      }
      case "Tab": {
        if (open) closeMenu();
        break;
      }
      default:
        break;
    }
  };

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || disabled) return;
    if (open) closeMenu();
    else openMenu();
  };

  const describedBy =
    [errorText ? errorId : null, helperText ? helperId : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div
      ref={containerRef}
      className={cx("go4-field", disabled && "go4-field--disabled", className)}
    >
      {label ? (
        <label
          id={labelId}
          htmlFor={dropdownId}
          className={cx("go4-field__label", isInvalid && "go4-field__label--invalid")}
        >
          {label}
        </label>
      ) : null}
      <div className="go4-dropdown">
        <button
          ref={setRef}
          type="button"
          id={dropdownId}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && activeIndex >= 0 ? `${dropdownId}-option-${activeIndex}` : undefined
          }
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          className={cx(
            "go4-dropdown__trigger",
            open && "go4-dropdown__trigger--open",
            isInvalid && "go4-dropdown__trigger--invalid",
          )}
          onClick={handleTriggerClick}
          onKeyDown={handleTriggerKeyDown}
          {...rest}
        >
          <span
            className={cx(
              "go4-dropdown__value",
              !selectedOption && "go4-dropdown__value--placeholder",
            )}
          >
            {selectedOption ? selectedOption.label : (placeholder ?? "")}
          </span>
          <span
            className={cx("go4-dropdown__caret", open && "go4-dropdown__caret--open")}
            aria-hidden="true"
          >
            <CaretIcon />
          </span>
        </button>
        {open ? (
          <ul
            id={listboxId}
            role="listbox"
            aria-labelledby={label ? labelId : undefined}
            className="go4-dropdown__menu"
          >
            {options.map((opt, index) => {
              const isSelected = opt.value === currentValue;
              const isActive = index === activeIndex;
              return (
                <li
                  key={opt.value}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  id={`${dropdownId}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={opt.disabled || undefined}
                  className={cx(
                    "go4-dropdown__item",
                    isActive && "go4-dropdown__item--active",
                    isSelected && "go4-dropdown__item--selected",
                    opt.disabled && "go4-dropdown__item--disabled",
                  )}
                  onMouseEnter={() => {
                    if (!opt.disabled) setActiveIndex(index);
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => commitValue(opt)}
                >
                  {isSelected ? <CheckIcon /> : null}
                  {opt.label}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {errorText ? (
        <p id={errorId} className="go4-field__error" role="alert">
          {errorText}
        </p>
      ) : helperText ? (
        <p id={helperId} className="go4-field__helper">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
